import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center bg-primary justify-center whitespace-nowrap rounded-[10px] text-sm font-semibold ring-offset-white transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-[linear-gradient(135deg,#f9cd4f_0%,#d4a012_100%)] text-[#111111] font-bold shadow-[0_0_24px_rgba(249,205,79,0.25),0_2px_8px_rgba(0,0,0,0.3)] hover:shadow-[0_0_30px_rgba(249,205,79,0.4),0_2px_8px_rgba(0,0,0,0.3)] transition-all duration-200 ease-out",
        destructive:
          "bg-[linear-gradient(135deg,#f9cd4f_0%,#d4a012_100%)] text-[#111111] hover:brightness-105 dark:bg-[linear-gradient(135deg,#f9cd4f_0%,#d4a012_100%)] dark:text-[#111111]",
        outline:
          "border border-[rgba(249,205,79,0.2)] bg-[rgba(249,205,79,0.05)] hover:bg-[rgba(249,205,79,0.1)] text-[#f9cd4f] transition-all duration-200 ease-out font-semibold",
        secondary:
          "border border-[rgba(249,205,79,0.2)] bg-[rgba(249,205,79,0.05)] hover:bg-[rgba(249,205,79,0.1)] text-[#f9cd4f] transition-all duration-200 ease-out font-semibold",
        ghost: "bg-transparent hover:bg-transparent text-foreground",
        link: "text-gray-900 underline-offset-4 hover:underline dark:text-black",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-[10px] px-3",
        lg: "h-11 rounded-[10px] px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
